// Admin management for the Associate Dashboard media flywheel. Media is
// managed the same way as every other BizConnect360 collateral (Marketing
// Materials, module cover images): the team uploads the file to Wibiz Media
// Storage themselves, then pastes the resulting URL here - see
// AdminMarketingMaterials.tsx's "Add Material" form for the exact same
// pattern this mirrors. There is no direct file upload in this admin form.

import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  GalleryHorizontal, Plus, Pencil, Trash2, ArrowUp, ArrowDown, PauseCircle, CheckCircle2,
  Loader2, Image as ImageIcon, FileText, Video, Youtube, Clapperboard, ExternalLink, Link as LinkIcon,
} from "lucide-react";

type FlywheelMediaType = "image" | "pdf" | "youtube" | "vimeo" | "video_url";

type FlywheelAdminItem = {
  id: number;
  title: string;
  description: string | null;
  mediaUrl: string;
  mediaType: FlywheelMediaType;
  clickThroughUrl: string | null;
  displayOrder: number;
  isActive: boolean;
};

const MEDIA_TYPES: { value: FlywheelMediaType; label: string; icon: any }[] = [
  { value: "image", label: "Image", icon: ImageIcon },
  { value: "pdf", label: "PDF / Document", icon: FileText },
  { value: "youtube", label: "YouTube", icon: Youtube },
  { value: "vimeo", label: "Vimeo", icon: Clapperboard },
  { value: "video_url", label: "Direct Video URL", icon: Video },
];
const MEDIA_TYPE_BY_VALUE = Object.fromEntries(MEDIA_TYPES.map(t => [t.value, t]));

type FormState = {
  title: string;
  description: string;
  mediaUrl: string;
  mediaType: FlywheelMediaType;
  clickThroughUrl: string;
};

const EMPTY_FORM: FormState = { title: "", description: "", mediaUrl: "", mediaType: "image", clickThroughUrl: "" };

// Reuses the existing app_settings key/value store (see appSettingsRouter in
// server/routers.ts) rather than adding new columns - the flywheel heading
// and subheading are just two more admin-configurable strings, the same
// pattern already used elsewhere for runtime settings.
const FLYWHEEL_HEADING_KEY = "flywheel_heading";
const FLYWHEEL_SUBHEADING_KEY = "flywheel_subheading";
const DEFAULT_FLYWHEEL_HEADING = "What's New";
const DEFAULT_FLYWHEEL_SUBHEADING = "Resources, promos, and updates from BizConnect360.";

function FlywheelHeadingSettings() {
  const utils = trpc.useUtils();
  const headingQuery = trpc.appSettings.get.useQuery({ key: FLYWHEEL_HEADING_KEY });
  const subheadingQuery = trpc.appSettings.get.useQuery({ key: FLYWHEEL_SUBHEADING_KEY });

  const [heading, setHeading] = useState("");
  const [subheading, setSubheading] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (headingQuery.data != null) setHeading(headingQuery.data);
  }, [headingQuery.data]);
  useEffect(() => {
    if (subheadingQuery.data != null) setSubheading(subheadingQuery.data);
  }, [subheadingQuery.data]);

  const setHeadingMutation = trpc.appSettings.set.useMutation({
    onSuccess: () => utils.appSettings.get.invalidate({ key: FLYWHEEL_HEADING_KEY }),
  });
  const setSubheadingMutation = trpc.appSettings.set.useMutation({
    onSuccess: () => utils.appSettings.get.invalidate({ key: FLYWHEEL_SUBHEADING_KEY }),
  });
  const saving = setHeadingMutation.isPending || setSubheadingMutation.isPending;

  const handleSave = async () => {
    try {
      await Promise.all([
        setHeadingMutation.mutateAsync({ key: FLYWHEEL_HEADING_KEY, value: heading.trim() || null }),
        setSubheadingMutation.mutateAsync({ key: FLYWHEEL_SUBHEADING_KEY, value: subheading.trim() || null }),
      ]);
      toast.success("Flywheel heading updated");
      setDirty(false);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm space-y-3">
      <div>
        <h3 className="text-sm font-bold text-gray-900">Flywheel Heading</h3>
        <p className="text-xs text-gray-500 mt-0.5">Shown above the rotating media on the Associate Dashboard overview.</p>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Heading</label>
        <Input
          value={heading}
          onChange={e => { setHeading(e.target.value); setDirty(true); }}
          placeholder={DEFAULT_FLYWHEEL_HEADING}
          className="text-sm"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Subheading</label>
        <Input
          value={subheading}
          onChange={e => { setSubheading(e.target.value); setDirty(true); }}
          placeholder={DEFAULT_FLYWHEEL_SUBHEADING}
          className="text-sm"
        />
      </div>
      <Button onClick={handleSave} disabled={!dirty || saving} className="bg-[#84ab4a] hover:bg-[#6d8f3a] text-white">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        <span className={saving ? "ml-1.5" : ""}>Save Heading</span>
      </Button>
    </div>
  );
}

// ─── Media field: paste a Wibiz Media Storage (or any http/https) link ────────
function FlywheelMediaField({
  mediaUrl, mediaType, onUrlChange, onTypeChange,
}: {
  mediaUrl: string;
  mediaType: FlywheelMediaType;
  onUrlChange: (url: string) => void;
  onTypeChange: (type: FlywheelMediaType) => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3">
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Media Type *</label>
        <Select value={mediaType} onValueChange={(v) => onTypeChange(v as FlywheelMediaType)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {MEDIA_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Media URL *</label>
        <Input
          placeholder="https://..."
          value={mediaUrl}
          onChange={(e) => onUrlChange(e.target.value)}
          className="text-sm"
        />
        <p className="text-[11px] text-gray-400 mt-1">
          Upload the image, PDF, or video to Wibiz Media Storage first, then paste the link here, the same way you already add other collateral (courses, materials, etc).
        </p>
      </div>
      {mediaUrl && mediaType === "image" && (
        <div className="sm:col-span-2 max-w-xs h-32 rounded-lg border border-gray-100 bg-gray-50 overflow-hidden flex items-center justify-center">
          <img src={mediaUrl} alt="Preview" className="w-full h-full object-contain" />
        </div>
      )}
      {mediaUrl && mediaType !== "image" && (
        <div className="sm:col-span-2">
          <a href={mediaUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#84ab4a] hover:underline">
            <ExternalLink className="w-3.5 h-3.5" /> Open link to confirm it's correct
          </a>
        </div>
      )}
    </div>
  );
}

function AdminFlywheelForm({
  form, setForm, onSubmit, onCancel, submitting, submitLabel,
}: {
  form: FormState;
  setForm: (updater: (f: FormState) => FormState) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitting: boolean;
  submitLabel: string;
}) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Title</label>
        <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. New Referral Bonus" className="text-sm" />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Description (optional)</label>
        <textarea
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Short caption shown under the slide"
          rows={2}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40 focus:border-[#84ab4a] resize-none"
        />
      </div>
      <FlywheelMediaField
        mediaUrl={form.mediaUrl}
        mediaType={form.mediaType}
        onUrlChange={(mediaUrl) => setForm(f => ({ ...f, mediaUrl }))}
        onTypeChange={(mediaType) => setForm(f => ({ ...f, mediaType }))}
      />
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Click-through URL (optional)</label>
        <div className="flex items-center gap-1.5">
          <LinkIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="https://..."
            value={form.clickThroughUrl}
            onChange={(e) => setForm(f => ({ ...f, clickThroughUrl: e.target.value }))}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#84ab4a]/40"
          />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button onClick={onSubmit} disabled={!form.title || !form.mediaUrl || submitting} className="bg-[#84ab4a] hover:bg-[#6d8f3a] text-white">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <GalleryHorizontal className="w-4 h-4" />}
          <span className="ml-1.5">{submitLabel}</span>
        </Button>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export function AdminFlywheelTab() {
  const utils = trpc.useUtils();
  const { data: items = [], isLoading } = trpc.flywheel.adminList.useQuery();

  const invalidate = () => utils.flywheel.adminList.invalidate();

  const createMutation = trpc.flywheel.create.useMutation({
    onSuccess: () => { invalidate(); toast.success("Flywheel item added"); setShowForm(false); setForm(EMPTY_FORM); },
    onError: (e) => toast.error(e.message),
  });
  const updateMutation = trpc.flywheel.update.useMutation({
    onSuccess: () => { invalidate(); toast.success("Flywheel item updated"); setEditingId(null); },
    onError: (e) => toast.error(e.message),
  });
  const toggleActiveMutation = trpc.flywheel.toggleActive.useMutation({
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e.message),
  });
  const deleteMutation = trpc.flywheel.delete.useMutation({
    onSuccess: () => { invalidate(); toast.success("Flywheel item deleted"); },
    onError: (e) => toast.error(e.message),
  });
  const reorderMutation = trpc.flywheel.reorder.useMutation({
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);

  const startEdit = (item: FlywheelAdminItem) => {
    setEditingId(item.id);
    setEditForm({
      title: item.title,
      description: item.description ?? "",
      mediaUrl: item.mediaUrl,
      mediaType: item.mediaType,
      clickThroughUrl: item.clickThroughUrl ?? "",
    });
  };

  const buildMutationInput = (f: FormState) => ({
    title: f.title,
    description: f.description || null,
    mediaUrl: f.mediaUrl,
    mediaType: f.mediaType,
    clickThroughUrl: f.clickThroughUrl || null,
  });

  const handleCreate = () => createMutation.mutate(buildMutationInput(form));
  const handleUpdate = (id: number) => updateMutation.mutate({ id, ...buildMutationInput(editForm) });

  const move = (index: number, dir: -1 | 1) => {
    const arr = [...(items as FlywheelAdminItem[])];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    reorderMutation.mutate({ items: arr.map((it, i) => ({ id: it.id, displayOrder: i })) });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-1">Media Flywheel</h2>
          <p className="text-sm text-gray-500">Manage the rotating promotional media shown on the Associate Dashboard overview. Upload media to Wibiz Media Storage, then paste the link here, the same way you manage other collateral.</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#84ab4a] hover:bg-[#6d8f3a] text-white gap-2">
          <Plus className="w-4 h-4" /> Add Item
        </Button>
      </div>

      <FlywheelHeadingSettings />

      {showForm && (
        <div className="bg-white border border-[#84ab4a]/30 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <GalleryHorizontal className="w-4 h-4 text-[#84ab4a]" />
            <span className="font-semibold text-gray-800">New Flywheel Item</span>
          </div>
          <AdminFlywheelForm
            form={form}
            setForm={setForm}
            onSubmit={handleCreate}
            onCancel={() => { setShowForm(false); setForm(EMPTY_FORM); }}
            submitting={createMutation.isPending}
            submitLabel="Add Item"
          />
        </div>
      )}

      {isLoading ? (
        <div className="py-12 text-center text-gray-400"><Loader2 className="w-6 h-6 mx-auto animate-spin opacity-40" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center">
          <GalleryHorizontal className="w-10 h-10 mx-auto mb-3 text-gray-200" />
          <p className="text-gray-500 font-medium">No flywheel items yet</p>
          <p className="text-sm text-gray-400 mt-1">Add your first item to start showing it on the Associate Dashboard.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {(items as FlywheelAdminItem[]).map((item, index) => {
            const typeInfo = MEDIA_TYPE_BY_VALUE[item.mediaType] ?? MEDIA_TYPES[0];
            const BadgeIcon = typeInfo.icon;
            const isEditing = editingId === item.id;
            return (
              <div key={item.id} className={`rounded-2xl border shadow-sm overflow-hidden bg-white border-gray-100 ${!item.isActive ? "opacity-50" : ""}`}>
                <div className="px-5 py-3 flex items-center gap-3 border-b border-gray-100">
                  <div className="flex flex-col gap-0.5 mr-1">
                    <button onClick={() => move(index, -1)} disabled={index === 0} className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 disabled:opacity-20 transition-colors" title="Move up">
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button onClick={() => move(index, 1)} disabled={index === items.length - 1} className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 disabled:opacity-20 transition-colors" title="Move down">
                      <ArrowDown className="w-3 h-3" />
                    </button>
                  </div>
                  <BadgeIcon className="w-4 h-4 flex-shrink-0 text-gray-400" />
                  <span className="font-semibold text-gray-900 text-sm flex-1 truncate">{item.title}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide bg-blue-50 text-blue-600">{typeInfo.label}</span>
                  {item.clickThroughUrl && (
                    <a href={item.clickThroughUrl} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-[#84ab4a]" title="Click-through link">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  {!item.isActive && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Hidden</span>}
                  <div className="flex items-center gap-1 ml-2">
                    <button onClick={() => isEditing ? setEditingId(null) : startEdit(item)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" title="Edit">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => toggleActiveMutation.mutate({ id: item.id, isActive: !item.isActive })} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" title={item.isActive ? "Deactivate" : "Activate"}>
                      {item.isActive ? <PauseCircle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => { if (confirm(`Delete "${item.title}"? This cannot be undone.`)) deleteMutation.mutate({ id: item.id }); }} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {isEditing ? (
                  <div className="px-5 py-4">
                    <AdminFlywheelForm
                      form={editForm}
                      setForm={setEditForm}
                      onSubmit={() => handleUpdate(item.id)}
                      onCancel={() => setEditingId(null)}
                      submitting={updateMutation.isPending}
                      submitLabel="Save Changes"
                    />
                  </div>
                ) : (
                  item.description && (
                    <div className="px-5 py-3 text-sm text-gray-600">{item.description}</div>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
